import { useCallback, useMemo } from 'react';

import {
  messageDepositCredited,
  messageDepositFailed,
} from '#/components/layout/notifications/notificationsRender';
import { displayBasicNotification } from '#/components/layout/notifications/notificationsUtils';
import config from '#/config';
import { filterFalsy } from '#/lib/utils';
import { useSelector } from '#/reduxState/store';

import { updatePendingDepositConfirmations } from './pendingDeposits';

const blockConfirmationDelay = config.chains.katanaChain.blocksConfirmation;

/** Get pending deposits list for active chain */
export const useSelectPendingDepositsListIncludingFailed = (filter?: {
  vaultManagerWallet: string;
}) => {
  const { pendingMap, pendingTxHashes } = useSelector(
    state => state.balances.pendingDeposits
  );
  return useMemo(() => {
    const pendingList = pendingTxHashes
      ? pendingTxHashes.map(hash => pendingMap[hash]).filter(filterFalsy)
      : [];
    if (!filter?.vaultManagerWallet) {
      return pendingList;
    }
    // Active filter
    return pendingList.filter(
      item =>
        item.vault?.manager?.toLowerCase() ===
        filter?.vaultManagerWallet.toLowerCase()
    );
  }, [pendingMap, pendingTxHashes, filter]);
};

export const useSelectPendingDepositListExcludingFailed = () => {
  const pendingDepositsList = useSelectPendingDepositsListIncludingFailed();
  const { getTransactionConfirmations } = usePendingDepositsConfirmations();
  return useMemo(
    () =>
      pendingDepositsList.filter(
        // Exclude failed deposits (-1 confirmations)
        item =>
          getTransactionConfirmations(item.sourceTxId).confirmationCount >= 0
      ),
    [getTransactionConfirmations, pendingDepositsList]
  );
};

export const usePendingDepositsConfirmations = () => {
  const depositsConfirmationsCounts = useSelector(
    state => state.balances.pendingDeposits.confirmationsCounts
  );

  const getTransactionConfirmations = useCallback(
    (transactionHash?: string | null) => {
      if (!transactionHash) {
        return {
          confirmationCount: 0,
          cappedConfirmationCount: 0,
          confirmationRate: 0,
        };
      }
      const confirmationCount =
        depositsConfirmationsCounts[transactionHash] || 0;
      const cappedConfirmationCount: number | undefined =
        transactionHash && blockConfirmationDelay
          ? Math.min(confirmationCount, blockConfirmationDelay)
          : undefined;
      const confirmationRate = blockConfirmationDelay
        ? (cappedConfirmationCount || 0) / blockConfirmationDelay
        : undefined;
      return {
        confirmationCount,
        cappedConfirmationCount,
        confirmationRate,
      };
    },
    [depositsConfirmationsCounts]
  );
  return {
    depositsConfirmationsCounts,
    blockConfirmationDelay,
    getTransactionConfirmations,
  };
};

export const notifyAboutCompletedDeposit = (quantity: string) => {
  return displayBasicNotification(
    messageDepositCredited({ time: Date.now(), quantity }),
    `depositCredited:${quantity}`,
    'success'
  );
};

export const notifyAndUpdatePendingDepositStatusToFailed = (txHash: string) => {
  displayBasicNotification(messageDepositFailed(), 'error');
  return updatePendingDepositConfirmations(txHash, -1);
};
